import { CohortBuilderModalLegacy, FilterTypeLegacy } from '@verantos-dev/types-cohorts';
import type {
  ActiveCohortBuilderItemLegacy,
  ActiveDerivedEventItemLegacy,
  ActiveIndexDateItemLegacy,
  ActiveSimpleEventItemLegacy,
  ActiveSubjectsVariableItemLegacy,
  FilterLegacy,
} from '@verantos-dev/types-cohorts';

import type {
  CohortDefinitionStoreActions,
  CohortDefinitionStoreState,
  QueuedEventModalSelection,
} from '../types';

type Store = CohortDefinitionStoreState & CohortDefinitionStoreActions;
type SetState = (
  partial: Store | Partial<Store> | ((state: Store) => Store | Partial<Store>)
) => void;
type GetState = () => Store;

export const createModalSlice = (set: SetState, get: GetState) => ({
  modal: undefined as CohortBuilderModalLegacy | undefined,
  queuedEventModals: undefined as QueuedEventModalSelection[] | undefined,
  queuedEventModalsConceptGroupId: undefined as string | undefined,
  activeCohortBuilderItem: undefined as ActiveCohortBuilderItemLegacy | undefined,
  viewedCohortBuilderItems: {} as Record<string, boolean>,

  setModal: ({ id, modal }: { id: string; modal: CohortBuilderModalLegacy }) => {
    const state = get();
    let activeCohortBuilderItem: ActiveCohortBuilderItemLegacy | undefined;

    if (
      modal === CohortBuilderModalLegacy.ADD_SIMPLE_EVENT ||
      modal === CohortBuilderModalLegacy.EDIT_SIMPLE_EVENT
    ) {
      const entity = state.simpleEvents.find((e) => e.id === id);
      if (entity) {
        activeCohortBuilderItem = {
          ...entity,
          temporalConstraints: [],
          eventFilters: [],
          countFilter: undefined,
          inputFieldConfigMap: {},
          timeWindow: undefined,
        } as unknown as ActiveSimpleEventItemLegacy;
      }
    } else if (
      modal === CohortBuilderModalLegacy.ADD_DERIVED_EVENT ||
      modal === CohortBuilderModalLegacy.EDIT_DERIVED_EVENT
    ) {
      const entity = state.derivedEvents.find((e) => e.id === id);
      if (entity) {
        activeCohortBuilderItem = {
          ...entity,
          conceptFilters: [],
          numericFilters: [],
          relatedEntityFilters: [],
          countFilter: undefined,
          inputFieldConfigMap: {},
          timeWindow: undefined,
        } as unknown as ActiveDerivedEventItemLegacy;
      }
    } else if (
      modal === CohortBuilderModalLegacy.ADD_SUBJECT_VARIABLE ||
      modal === CohortBuilderModalLegacy.EDIT_SUBJECT_VARIABLE
    ) {
      const entity = state.subjectVariables.find((e) => e.id === id);
      if (entity) {
        activeCohortBuilderItem = {
          ...entity,
          temporalConstraints: [],
          eventFilters: [],
          inputFieldConfigMap: {},
        } as unknown as ActiveSubjectsVariableItemLegacy;
      }
    } else if (
      modal === CohortBuilderModalLegacy.ADD_INDEX_DATE ||
      modal === CohortBuilderModalLegacy.EDIT_INDEX_DATE
    ) {
      if (state.indexDate) {
        activeCohortBuilderItem = {
          ...state.indexDate,
          name: 'Index date',
          conceptFilters: [],
          numericFilters: [],
          temporalConstraints: [],
          eventFilters: [],
          countFilter: undefined,
          inputFieldConfigMap: {},
          timeWindow: undefined,
        } as unknown as ActiveIndexDateItemLegacy;
      }
    }

    set({ modal, activeCohortBuilderItem });
  },

  clearModal: () => set({ modal: undefined, activeCohortBuilderItem: undefined }),

  queueEventModals: (selections: QueuedEventModalSelection[], conceptGroupId: string | undefined) =>
    set({
      queuedEventModals: selections,
      queuedEventModalsConceptGroupId: conceptGroupId,
    }),

  clearQueuedEventModals: () =>
    set({ queuedEventModals: undefined, queuedEventModalsConceptGroupId: undefined }),

  setActiveCohortBuilderItemFilterInputFieldValue: ({
    filterType,
    filterId,
    inputFieldId,
    value,
  }: {
    filterType: FilterTypeLegacy;
    filterId: string;
    inputFieldId: string;
    value: string;
  }) =>
    set((state) => {
      const item = state.activeCohortBuilderItem;
      if (!item) return state;

      const updateFilters = (filters: FilterLegacy[]): FilterLegacy[] =>
        filters.map((filter) => {
          if (filter.id !== filterId) return filter;
          const inputFields = filter.inputFields.map((f) =>
            f.id === inputFieldId ? { ...f, value, dirty: true } : f
          );
          return { ...filter, inputFields, updated: inputFieldId };
        });

      switch (filterType) {
        case FilterTypeLegacy.CONCEPT:
          return {
            activeCohortBuilderItem: {
              ...item,
              conceptFilters: updateFilters(item.conceptFilters),
            },
          };
        case FilterTypeLegacy.NUMERIC:
          return {
            activeCohortBuilderItem: {
              ...item,
              numericFilters: updateFilters(item.numericFilters),
            },
          };
        case FilterTypeLegacy.RELATED_ENTITY:
          return {
            activeCohortBuilderItem: {
              ...item,
              relatedEntityFilters: updateFilters(item.relatedEntityFilters ?? []),
            },
          };
        case FilterTypeLegacy.TEMPORAL:
          return {
            activeCohortBuilderItem: {
              ...item,
              temporalConstraints: updateFilters(item.temporalConstraints),
            },
          };
        case FilterTypeLegacy.UNIQUENESS:
          return {
            activeCohortBuilderItem: {
              ...item,
              uniquenessFilters: updateFilters(item.uniquenessFilters ?? []),
            },
          };
        case FilterTypeLegacy.COUNT: {
          if (!item.countFilter) return state;
          const [updated] = updateFilters([item.countFilter]);
          return { activeCohortBuilderItem: { ...item, countFilter: updated } };
        }
        default:
          return state;
      }
    }),

  markItemViewed: (id: string) =>
    set((state) => ({
      viewedCohortBuilderItems: { ...state.viewedCohortBuilderItems, [id]: true },
    })),
});
